
import { elem, styleInstaller } from "./chrome";

const STYLE_ID = "metin3-tips-style";

/** `uitip.py:37`, `TipBoard.TIP_DURATION = 5.0`. */
const DEFAULT_DURATION_MS = 5_000;

/** `uitip.py:157`, `BigBoard.TIP_DURATION = 10.0`. */
export const BIG_NOTICE_DURATION_MS = 10_000;

export const BIG_SCROLL_WAIT_MS = 5_000;

export const BIG_FIRST_HOLD_MS = 1_000;

export const NOTICE_CAPACITY = 4;

/** The fraction of a row's life after which it is drawn quieter. */
const FADE_AFTER = 0.7;

/**
 * `BigBoard.__CreateTextBar` sets `SetTextColor(242, 231, 193)` (`uitip.py:209`)
 * and `BigBoard.LINE_WIDTH` is 500 with `FONT_HEIGHT` 18 (`:158-160`).
 */
const STYLE = `
.tip-board {
  position: fixed;
  top: calc(76px + env(safe-area-inset-top, 0px));
  left: 50%;
  transform: translateX(-50%);
  z-index: 70;
  display: grid;
  gap: 6px;
  justify-items: center;
  width: min(560px, calc(100vw - 24px));
  margin: 0;
  padding: 0;
  pointer-events: none;
  font: 14px/1.35 system-ui, -apple-system, "Segoe UI", sans-serif;
}
.tip-rail { display: contents; }
.tip-item {
  display: flex;
  align-items: baseline;
  gap: 8px;
  max-width: 100%;
  padding: 6px 12px 7px;
  border: 1px solid var(--intro-edge-soft, #3b3524);
  border-left: 2px solid var(--intro-gold, #e8c877);
  border-radius: var(--intro-radius, 3px);
  background: var(--intro-board-fill, rgba(0, 0, 0, 0.51));
  color: var(--intro-ink-bright, #e6e0d0);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.9);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.45);
  transition: opacity 260ms linear;
}
.tip-item[data-age="fading"] { opacity: 0.5; }
.tip-line { min-width: 0; overflow-wrap: anywhere; }
.tip-count {
  flex: none;
  align-self: center;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: var(--intro-gold-dim, #a89a72);
}
.notice-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 71;
  padding: calc(9px + env(safe-area-inset-top, 0px)) 16px 10px;
  border-bottom: 1px solid var(--intro-edge-strong, #6b5a2f);
  background: linear-gradient(rgba(10, 9, 6, 0.94), rgba(10, 9, 6, 0.78));
  color: #f2e7c1;
  text-align: center;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.95);
  pointer-events: none;
  transition: opacity 260ms linear;
}
.notice-banner-text {
  margin: 0 auto;
  max-width: min(560px, calc(100vw - 32px));
  font: 600 18px/1.3 system-ui, -apple-system, "Segoe UI", sans-serif;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
}
/*
 * There is no entrance animation here, and that is a finding rather than an
 * omission.
 *
 * There were two, and on the running client neither ever ran. Measured through
 * getComputedStyle in the live page, with a real GM notice on screen: the
 * banner sat at opacity: 0 and y = -43, and every row at opacity: 0 --
 * each held at 0% of an animation that had been "starting" for several
 * seconds. The bar measured 560x74 at the top of the screen, held exactly the
 * right text, passed every assertion about its own DOM, and photographed as an
 * empty screen three runs in a row.
 *
 * Two things came out of that. The first is a rule: animation-fill-mode:
 * both applies an animation's opening frame *before* it starts, so an
 * animation that never gets to run leaves its subject in its own first frame
 * for ever -- never put opacity: 0 in one on this page. The second is the
 * measurement that followed. With the fill mode dropped and the keyframes
 * reduced to a six-pixel slide, the banner still measured y = -6: not a
 * missing start, an animation genuinely pinned at 0% in a client whose main
 * thread is a WebGL render loop.
 *
 * So the decoration is gone rather than nursed. What is left is one
 * transition on the ageing dim, whose worst case under the same conditions
 * is that a row snaps to its correct final opacity instead of easing to it --
 * a transition cannot strand its subject in a frame the way a filled animation
 * can. tools/e2e/notice_bar.mjs measures the box, the position and the
 * opacity of both surfaces on the running client, because "the element is in
 * the DOM with the right text" was true the whole time this was broken.
 */
@media (prefers-reduced-motion: reduce) {
  .tip-item, .notice-banner { transition: none; }
}
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

/** Values below zero do not keep a board alive forever. */
export function tipDuration(durationMs: number): number {
  return Number.isFinite(durationMs) && durationMs > 0 ? durationMs : DEFAULT_DURATION_MS;
}

/** How much of its life a row has spent. */
export type NoticeAge = "fresh" | "fading";

export interface NoticeRow {
  /** The server's sentence, verbatim. */
  readonly text: string;
  /** How many times it has arrived while this row was alive. */
  readonly count: number;
  /** When the most recent occurrence arrived; the row ages from here. */
  readonly at: number;
}

export class NoticeQueue {
  private queue: NoticeRow[] = [];

  constructor(private readonly lifetimeMs: number = DEFAULT_DURATION_MS) {}

  get rows(): readonly NoticeRow[] {
    return this.queue;
  }

  get nextExpiryAt(): number | null {
    let oldest = Number.POSITIVE_INFINITY;
    for (const row of this.queue) if (row.at < oldest) oldest = row.at;
    return oldest === Number.POSITIVE_INFINITY ? null : oldest + this.lifetimeMs;
  }

  push(text: string, now: number): void {
    if (text.length === 0) return;
    // One pass, not a `find` and then a `filter` over the same predicate: the
    // count of the row being replaced and the list without it are the same
    // walk.
    let count = 1;
    const kept: NoticeRow[] = [];
    for (const row of this.queue) {
      if (row.text === text) count = row.count + 1;
      else kept.push(row);
    }
    kept.push({ text, count, at: now });
    this.queue = kept;
    // The oldest goes, never the newest: the newest is the one the player has
    // not read yet.
    if (this.queue.length > NOTICE_CAPACITY) this.queue = this.queue.slice(-NOTICE_CAPACITY);
  }

  /** Drop every row whose lifetime has run out at `now`. */
  expire(now: number): void {
    this.queue = this.queue.filter((row) => now - row.at < this.lifetimeMs);
  }

  age(row: NoticeRow, now: number): NoticeAge {
    return now - row.at >= this.lifetimeMs * FADE_AFTER ? "fading" : "fresh";
  }

  clear(): void {
    this.queue = [];
  }
}

export class TipBoard {
  private readonly root: HTMLElement;
  private readonly rail: HTMLElement;
  private readonly banner: HTMLElement;
  private readonly bannerText: HTMLElement;
  private readonly queue: NoticeQueue;
  /**
   * `BigBoard.tipList` (`uitip.py:171`): the big-notice backlog, one page per
   * entry, each living `TIP_DURATION = 10.0` (`:157`).
   */
  private readonly banners: NoticeQueue;
  private readonly nodes = new Map<string, HTMLElement>();
  /** Which page of `banners` is on screen, by its text. */
  private bannerShown = "";
  /** `nextScrollTime` (`uitip.py:255`): when the board pages on by itself. */
  private bannerScrollAt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private clock = 0;

  constructor(
    container: HTMLElement,
    durationMs = DEFAULT_DURATION_MS,
    private readonly isNoticeVisible: () => boolean = () => true,
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);
    this.queue = new NoticeQueue(tipDuration(durationMs));
    this.banners = new NoticeQueue(BIG_NOTICE_DURATION_MS);
    this.root = elem(doc, "div", "tip-board");
    this.root.hidden = true;
    this.root.setAttribute("role", "status");
    this.root.setAttribute("aria-live", "polite");
    // Without this the whole stack is re-read every time one row joins it.
    this.root.setAttribute("aria-atomic", "false");
    this.rail = elem(doc, "div", "tip-rail");
    this.root.append(this.rail);
    this.banner = elem(doc, "div", "notice-banner");
    this.banner.hidden = true;
    this.banner.setAttribute("role", "status");
    this.banner.setAttribute("aria-live", "polite");
    this.bannerText = elem(doc, "p", "notice-banner-text");
    this.banner.append(this.bannerText);
    container.append(this.root, this.banner);
  }

  get showing(): boolean { return !this.root.hidden; }
  get element(): HTMLElement { return this.root; }
  /** The rows on screen. A repeated sentence is one of them, not two. */
  get pending(): number { return this.queue.rows.length; }
  /** How many big notices are queued behind the banner. `notice_bar.mjs`. */
  get bannerPending(): number { return this.banners.rows.length; }

  /** Add a notice to the stack. A notice never replaces the one before it. */
  push(text: string, now?: number): void {
    const at = this.at(now);
    this.queue.push(text, at);
    this.draw(at);
    this.schedule();
  }

  setTip(text: string): void {
    if (!this.isNoticeVisible()) return;
    this.push(text);
  }

  big(text: string, now?: number): void {
    if (text.length === 0) return;
    if (!this.isNoticeVisible()) return;
    const at = this.at(now);
    this.banners.push(text, at);
    this.bannerShown = text;
    this.bannerScrollAt = at + BIG_FIRST_HOLD_MS;
    this.drawBanner();
    this.schedule();
  }

  private drawBanner(): void {
    const rows = this.banners.rows;
    if (rows.length === 0) {
      this.bannerShown = "";
      this.banner.hidden = true;
      this.bannerText.textContent = "";
      return;
    }
    const at = rows.findIndex((row) => row.text === this.bannerShown);
    const page = at < 0 ? rows[0] : rows[at];
    this.bannerShown = page.text;
    if (this.bannerText.textContent !== page.text) this.bannerText.textContent = page.text;
    this.banner.hidden = false;
  }

  private advanceBanner(now: number): void {
    const rows = this.banners.rows;
    if (rows.length <= 1) return;
    const at = rows.findIndex((row) => row.text === this.bannerShown);
    this.bannerShown = rows[(at + 1) % rows.length].text;
    this.bannerScrollAt = now + BIG_SCROLL_WAIT_MS;
  }

  /** Take both surfaces down. */
  clear(): void {
    this.clearLines();
    this.banners.clear();
    this.bannerScrollAt = 0;
    this.drawBanner();
    this.stopTimer();
  }

  tick(now = Date.now()): void {
    const at = this.at(now);
    this.queue.expire(at);
    this.draw(at);
    // Expire first, then page: an entry whose ten seconds are up must not get
    // one more page out of the advance that happens in the same tick.
    this.banners.expire(at);
    if (this.bannerScrollAt !== 0 && at >= this.bannerScrollAt) this.advanceBanner(at);
    this.drawBanner();
    this.schedule();
  }

  private clearLines(): void {
    this.queue.clear();
    this.rail.replaceChildren();
    this.nodes.clear();
    this.root.hidden = true;
  }

  private at(now?: number): number {
    const value = now ?? Math.max(Date.now(), this.clock);
    this.clock = Math.max(this.clock, value);
    return value;
  }

  private draw(now: number): void {
    const doc = this.rail.ownerDocument;
    const rows = this.queue.rows;
    const live = new Set(rows.map((row) => row.text));
    for (const [text, node] of [...this.nodes]) {
      if (live.has(text)) continue;
      node.remove();
      this.nodes.delete(text);
    }
    rows.forEach((row, index) => {
      let node = this.nodes.get(row.text);
      if (node === undefined) {
        node = elem(doc, "div", "tip-item");
        node.append(elem(doc, "div", "tip-line", row.text), elem(doc, "span", "tip-count"));
        this.nodes.set(row.text, node);
        this.rail.append(node);
      } else if (this.rail.children[index] !== node) {
        // Only a repeat reorders, and only to the newest position.
        this.rail.append(node);
      }
      node.dataset.repeat = String(row.count);
      node.dataset.age = this.queue.age(row, now);
      const count = node.querySelector<HTMLElement>(".tip-count");
      if (count !== null) {
        count.textContent = row.count > 1 ? `×${row.count}` : "";
        count.hidden = row.count <= 1;
      }
    });
    this.root.hidden = rows.length === 0;
  }

  /** Arm one timer for the next moment anything changes by itself. */
  private schedule(): void {
    this.stopTimer();
    // The next moment any of the three clocks moves: a row expiring, a big
    // notice expiring, or the big board paging on. Only armed when there is
    // more than one page to reach, because a backlog of one has nothing to
    // scroll to.
    let wake = Number.POSITIVE_INFINITY;
    const rowExpiry = this.queue.nextExpiryAt;
    if (rowExpiry !== null && rowExpiry < wake) wake = rowExpiry;
    const bigExpiry = this.banners.nextExpiryAt;
    if (bigExpiry !== null && bigExpiry < wake) wake = bigExpiry;
    if (this.banners.rows.length > 1 && this.bannerScrollAt < wake) wake = this.bannerScrollAt;
    if (wake === Number.POSITIVE_INFINITY) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.tick();
    }, Math.max(0, wake - Math.max(Date.now(), this.clock)));
  }

  private stopTimer(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }
}
